import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Box, Button, IconButton, useTheme } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';
import dayjs, { Dayjs } from 'dayjs';

interface DateRangePickerProps {
  value: { from: string; to: string };
  onChange: (value: { from: string; to: string }) => void;
  placeholder?: string;
}

interface Preset {
  key: string;
  label: string;
  from: string;
  to: string;
}

const DateRangePicker = ({
  value,
  onChange,
  placeholder = 'Select date range',
}: DateRangePickerProps) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const [draftFrom, setDraftFrom] = useState(value.from);
  const [draftTo, setDraftTo] = useState(value.to);
  const [hoverDate, setHoverDate] = useState<Dayjs | null>(null);
  const [anchoring, setAnchoring] = useState(false);
  const [monthRef, setMonthRef] = useState(dayjs().startOf('month'));
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number } | null>(null);

  // Presets (excluding today)
  const presets: Preset[] = [
    {
      key: 'yesterday',
      label: 'Yesterday',
      from: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
      to: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    },
    {
      key: 'd3',
      label: 'Last 3 Days',
      from: dayjs().subtract(3, 'day').format('YYYY-MM-DD'),
      to: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    },
    {
      key: 'd7',
      label: 'Last 7 Days',
      from: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
      to: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    },
    {
      key: 'd14',
      label: 'Last 14 Days',
      from: dayjs().subtract(14, 'day').format('YYYY-MM-DD'),
      to: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    },
    {
      key: 'mThis',
      label: 'This Month (to yesterday)',
      from: dayjs().startOf('month').format('YYYY-MM-DD'),
      to: dayjs().subtract(1, 'day').format('YYYY-MM-DD'),
    },
    {
      key: 'mPrev',
      label: 'Previous Month',
      from: dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD'),
      to: dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD'),
    },
  ];

  useEffect(() => {
    setDraftFrom(value.from);
    setDraftTo(value.to);
  }, [value.from, value.to]);

  // Calculate dropdown position when opened
  useEffect(() => {
    if (open && rootRef.current) {
      const rect = rootRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
      });
    }
  }, [open]);

  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (
        rootRef.current &&
        dialogRef.current &&
        !rootRef.current.contains(e.target as Node) &&
        !dialogRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
        setDropdownPosition(null);
      }
    };
    if (open) document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const apply = (next?: { from: string; to: string }) => {
    let from = next?.from ?? draftFrom;
    let to = next?.to ?? draftTo;

    if (from && to && from > to) {
      [from, to] = [to, from];
    }

    onChange({ from, to });
    setOpen(false);
    setDropdownPosition(null);
  };

  const onDayClick = (day: Dayjs) => {
    if (!anchoring) {
      setAnchoring(true);
      setDraftFrom(day.format('YYYY-MM-DD'));
      setDraftTo('');
    } else {
      let from = draftFrom || day.format('YYYY-MM-DD');
      let to = day.format('YYYY-MM-DD');

      if (from > to) {
        [from, to] = [to, from];
      }

      setDraftFrom(from);
      setDraftTo(to);
      setAnchoring(false);
    }
  };

  const handlePreset = (preset: Preset) => {
    setDraftFrom(preset.from);
    setDraftTo(preset.to);
    apply({ from: preset.from, to: preset.to });
  };

  // Generate calendar days
  const generateDays = () => {
    const firstDay = monthRef.startOf('month');
    const offset = firstDay.day(); // 0 = Sunday
    const gridStart = firstDay.subtract(offset, 'day');
    return Array.from({ length: 42 }, (_, i) => gridStart.add(i, 'day'));
  };

  const days = generateDays();
  const fDate = draftFrom ? dayjs(draftFrom) : null;
  const tDate = draftTo ? dayjs(draftTo) : null;

  const previewTo = anchoring && hoverDate && fDate ? (hoverDate.isBefore(fDate) ? fDate : hoverDate) : null;
  const previewFrom = anchoring && hoverDate && fDate ? (hoverDate.isBefore(fDate) ? hoverDate : fDate) : null;

  const activePresetKey = presets.find(
    (p) => p.from === (draftFrom || value.from) && p.to === (draftTo || value.to)
  )?.key;

  return (
    <Box ref={rootRef} sx={{ position: 'relative', display: 'inline-block' }}>
      <Button
        onClick={() => setOpen(!open)}
        endIcon={<CalendarIcon sx={{ fontSize: 16 }} />}
        sx={{
          minWidth: 280,
          height: 40,
          justifyContent: 'space-between',
          textAlign: 'left',
          color: theme.palette.text.primary,
          borderColor: theme.palette.divider,
          backgroundColor: '#fff',
          textTransform: 'none',
          borderRadius: '6px',
          padding: '0 16px',
          fontSize: '14px',
          '&:hover': {
            borderColor: theme.palette.primary.main,
            backgroundColor: `${theme.palette.primary.main}05`,
          },
        }}
        variant="outlined"
      >
        {draftFrom && draftTo ? `${draftFrom} — ${draftTo}` : placeholder}
      </Button>

      {open && dropdownPosition && createPortal(
        <Box
          ref={dialogRef}
          sx={{
            position: 'fixed',
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            zIndex: 99999,
            width: 520,
            borderRadius: '10px',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: '#fff',
            boxShadow: '0 10px 30px rgba(146,153,184,0.15)',
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderBottom: `1px solid ${theme.palette.divider}`,
              p: 1.5,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setMonthRef(monthRef.subtract(1, 'month'))}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '6px',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}08`,
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Box sx={{ fontWeight: 600, fontSize: '15px' }}>
              {monthRef.format('MMMM YYYY')}
            </Box>
            <IconButton
              size="small"
              onClick={() => setMonthRef(monthRef.add(1, 'month'))}
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '6px',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: `${theme.palette.primary.main}08`,
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Calendar and Presets */}
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 180px', gap: 1.5, p: 2 }}>
            {/* Calendar */}
            <Box>
              {/* Weekday headers */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(7, 1fr)',
                  gap: 0.5,
                  textAlign: 'center',
                  mb: 0.5,
                  fontSize: 12,
                  color: theme.palette.text.secondary,
                }}
              >
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                  <Box key={d}>{d}</Box>
                ))}
              </Box>

              {/* Days */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
                {days.map((day, idx) => {
                  const inMonth = day.month() === monthRef.month();
                  const isStart = fDate && day.isSame(fDate, 'day');
                  const isEnd = tDate && day.isSame(tDate, 'day');
                  const inSel = fDate && tDate && day.isAfter(fDate) && day.isBefore(tDate);
                  const inPreview =
                    previewFrom && previewTo && day.isAfter(previewFrom) && day.isBefore(previewTo);

                  return (
                    <Button
                      key={idx}
                      onMouseEnter={() => setHoverDate(day)}
                      onMouseLeave={() => setHoverDate(null)}
                      onClick={() => onDayClick(day)}
                      sx={{
                        minWidth: 0,
                        height: 36,
                        p: 0,
                        borderRadius: '6px',
                        fontSize: 13,
                        color: inMonth ? theme.palette.text.primary : theme.palette.text.disabled,
                        fontWeight: isStart || isEnd ? 600 : 400,
                        backgroundColor:
                          inSel || inPreview
                            ? `${theme.palette.primary.main}15`
                            : 'transparent',
                        border:
                          anchoring && hoverDate && day.isSame(hoverDate, 'day')
                            ? `2px solid ${theme.palette.primary.main}`
                            : '1px solid transparent',
                        '&:hover': {
                          backgroundColor: `${theme.palette.primary.main}10`,
                        },
                      }}
                    >
                      {day.date()}
                    </Button>
                  );
                })}
              </Box>
            </Box>

            {/* Presets */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {presets.map((preset) => {
                const active = activePresetKey === preset.key;
                return (
                  <Button
                    key={preset.key}
                    onClick={() => handlePreset(preset)}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      borderRadius: '6px',
                      fontSize: '13px',
                      padding: '8px 12px',
                      border: active
                        ? `1px solid ${theme.palette.primary.main}`
                        : `1px solid ${theme.palette.divider}`,
                      backgroundColor: active ? `${theme.palette.primary.main}15` : '#fff',
                      color: active ? theme.palette.primary.main : theme.palette.text.primary,
                      fontWeight: active ? 600 : 400,
                      '&:hover': {
                        backgroundColor: `${theme.palette.primary.main}10`,
                        borderColor: theme.palette.primary.main,
                      },
                    }}
                  >
                    {preset.label}
                  </Button>
                );
              })}

              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  onClick={() => {
                    setOpen(false);
                    setDropdownPosition(null);
                  }}
                  variant="outlined"
                  size="small"
                  fullWidth
                  sx={{ borderRadius: '6px', fontSize: '13px' }}
                >
                  Close
                </Button>
                <Button
                  onClick={() => apply()}
                  variant="contained"
                  size="small"
                  fullWidth
                  sx={{ borderRadius: '6px', fontSize: '13px' }}
                >
                  Apply
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>,
        document.body
      )}
    </Box>
  );
};

export default DateRangePicker;
